import api from '@sparemin/api-sdk';
import { merge } from 'lodash';
import mixpanel, { Dict } from 'mixpanel-browser';
import { getMarketingParamsFromUrl } from 'utils/dom';
import { pascalKeys } from 'utils/object';
import {
  ClearUserTrackingInfo,
  SetUserTrackingInfo,
  Sound,
  TrackExperiments,
  TrackPlaybackOptions,
} from '../types';

export function getSound(volume: number): Sound {
  return volume > 0 ? 'Unmuted' : 'Muted';
}

const transform = (properties?: Dict) => {
  const transformedProperties = !properties
    ? undefined
    : pascalKeys(properties);
  return transformedProperties;
};

export const track: typeof mixpanel.track = (
  eventName,
  properties,
  optionsOrCallback,
  callback,
) => {
  const transformedProperties = transform(properties);

  mixpanel.track(eventName, transformedProperties, optionsOrCallback, callback);
};

export const trackMake: typeof mixpanel.make.track = (
  eventName,
  properties,
  optionsOrCallback,
  callback,
) => {
  const transformedProperties = transform(properties);

  mixpanel.make.track(
    eventName,
    transformedProperties,
    optionsOrCallback,
    callback,
  );
};

/**
 * Tracks mixpanel events by sending them to our API, which then forwards them
 * to Mixpanel.  This is generally used for debugging and high-priority events
 * that we want to ensure are tracked
 */
export const apiTrack = (eventName: string, properties: object) => {
  const eventProperties = merge(
    {},
    // add all of the properties specific to this event
    transform(properties),
    // utm properties from the URL
    getMarketingParamsFromUrl(),
    // since MP isn't handling this event, add all of the super properties that
    // MP would have sent.  this includes persisted utm properties
    // https://github.com/mixpanel/mixpanel-js/blob/3623fe0132860386eeed31756e0d7eb4e61997ed/src/mixpanel-core.js#L930
    mixpanel.persistence.properties(),
    mixpanel.unpersisted_superprops,
  );

  api.forwardMixpanelEvent({
    project: 'play',
    distinctId: mixpanel.get_distinct_id(),
    eventName,
    properties: eventProperties,
    referrer: document.referrer,
  });
};

export const register: typeof mixpanel.register = (props, days) => {
  mixpanel.register(pascalKeys(props), days);
};

export const trackExperiments: TrackExperiments = (
  experiments: Record<string, string>,
) => {
  mixpanel.register(experiments);
};

export const setUserTrackingInfo: SetUserTrackingInfo = (
  email: string,
  userId: number,
) => {
  mixpanel.identify(email);
  mixpanel.people.set({
    $email: email,
    last_login: new Date(),
    user_id: userId,
  });
  mixpanel.people.set_once('firstLogin', new Date().toISOString());
};

export const clearUserTrackingInfo: ClearUserTrackingInfo = () =>
  mixpanel.reset();

export const getPlaybackPropsFromSource = (props: TrackPlaybackOptions) => {
  const { source, podcastName, episodeName } = props;
  if (source !== 'PodcastPage' && source !== 'HomePage') {
    return { source, podcastName, episodeName };
  }
  const { sound, clipId } = props;

  if (source === 'PodcastPage') {
    return {
      source: 'PodcastPageClip',
      sound: getSound(sound),
      podcastName,
      episodeName,
      clipId,
    };
  }
  return {
    source: 'HomePageClip',
    sound: getSound(sound),
    podcastName,
    episodeName,
    clipId,
  };
};
